/**
 * Request/response schemas for provider e-signature capture — the mark (drawn or adopted-font) and
 * the admin-controlled signature line, `emr-app` direct instruction 2026-08-18.
 *
 * Same split as `encounterRequests.ts`: this file holds the wire shapes both `server/` and `web/`
 * validate against, so the two cannot drift; `server/src/db/schema/signatures.ts` holds the storage
 * shape (one `user_signatures` row per org+user), and `server/src/auth/signatureService.ts` holds
 * the write rules.
 *
 * **Two separate write surfaces, on purpose.** `updateSignatureSettingsRequestSchema` is the
 * self-service body — method plus mark fields, and nothing else. `signatureLine` does not appear in
 * it at all; a caller cannot even attempt to set their own line through this schema, let alone have
 * it silently accepted, matching `encounterRequests.ts`'s own "no server-owned value appears in any
 * request schema" discipline one level further — here the value is not server-owned, it is
 * *someone-else*-owned, which is the same shape of problem this codebase already solves for
 * `authorId`/`signedBy`/`cosignedBy` by leaving them out of the request entirely rather than trusting
 * a role check to catch a body that included them. `updateSignatureLineRequestSchema` is the
 * separate, admin-only body for exactly that one field, used by a different route gated on
 * `org:manage_members`.
 */

import { z } from 'zod';

/** `@emr/shared`'s closed set for how a person's signature mark was produced — matches
 * `db/schema/signatures.ts`'s `userSignatures.method`. */
export const SIGNATURE_METHODS = ['drawn', 'font'] as const;
export type SignatureMethod = (typeof SIGNATURE_METHODS)[number];

/**
 * The bundled handwriting-style fonts, self-hosted under `web/public/fonts/` to satisfy this
 * project's CSP (`font-src 'self'`, no external font host) — see `web/src/signature/fonts.ts`'s own
 * header comment for the license (SIL Open Font License 1.1) each file carries and where it came
 * from. Each id here
 * is both the wire value stored in `userSignatures.fontName` and the key `web/src/signature/fonts.ts`
 * maps to a real `font-family`/file — adding a fifth font is a two-line change in both places, not a
 * schema migration, since the column is plain `text`.
 */
export const SIGNATURE_FONTS = ['dancing-script', 'great-vibes', 'caveat', 'sacramento'] as const;
export type SignatureFont = (typeof SIGNATURE_FONTS)[number];

export const MAX_FONT_TEXT_LENGTH = 100;
export const MAX_SIGNATURE_LINE_LENGTH = 200;

/** One pen-stroke point, `[x, y]` normalized to a 0..1 canvas-relative box — resolution- and
 * DPI-independent, matching `calendarDate`'s own "never converted" discipline for a different axis:
 * a value stored this way renders identically on the phone it was drawn on and the desktop a
 * co-signer later reviews it from. */
const strokePointSchema = z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)]);

/** One continuous pen stroke: at least 2 points (a single point is a dot, not a stroke — the drawing
 * UI can still send one if that is genuinely all a person drew, so the floor is 1, not 2, to avoid
 * silently discarding a legitimate short mark). Capped well above what a real signature needs, as a
 * defence against an unbounded body, matching `encounterRequests.ts`'s reasoning for
 * `MAX_NOTE_CONTENT_LENGTH`. */
const strokeSchema = z.array(strokePointSchema).min(1).max(1000);

/** A full drawn signature: one or more strokes (a pen-up/pen-down sequence), capped the same way. */
export const drawnSignatureStrokesSchema = z.array(strokeSchema).min(1).max(50);
export type DrawnSignatureStrokes = z.infer<typeof drawnSignatureStrokesSchema>;

/**
 * The self-service mark update. A discriminated union on `method` rather than one object with every
 * field optional — `zod`'s own mechanism for "these fields are required together, and only these
 * fields", so a `'font'` body with stray `drawnStrokes` is a rejection rather than a silently
 * ignored extra (both variants are `strictObject`s for the same reason `authRequests.ts` uses them
 * throughout).
 */
export const updateSignatureSettingsRequestSchema = z.discriminatedUnion('method', [
  z.strictObject({
    method: z.literal('drawn'),
    drawnStrokes: drawnSignatureStrokesSchema,
  }),
  z.strictObject({
    method: z.literal('font'),
    fontName: z.enum(SIGNATURE_FONTS),
    fontText: z.string().trim().min(1).max(MAX_FONT_TEXT_LENGTH),
  }),
]);
export type UpdateSignatureSettingsRequest = z.infer<typeof updateSignatureSettingsRequestSchema>;

/** The admin-only write. `signatureLine: null` clears it — distinct from omitting the field, which
 * `PUT`-shaped semantics here treat as "nothing to change" is not offered at all: this is the only
 * field the route accepts, so there is nothing an omitted field could mean. */
export const updateSignatureLineRequestSchema = z.strictObject({
  signatureLine: z.string().trim().min(1).max(MAX_SIGNATURE_LINE_LENGTH).nullable(),
});
export type UpdateSignatureLineRequest = z.infer<typeof updateSignatureLineRequestSchema>;

/**
 * The full current settings for one person — what `GET /api/profile/signature` returns, and the
 * shape `signedSignatureSnapshot`/`cosignedSignatureSnapshot` on a note copy at signing time
 * (`db/schema/clinical.ts`'s own comment on why that copy is never a live reference back to this).
 */
export const signatureSettingsSchema = z.strictObject({
  method: z.enum(SIGNATURE_METHODS).nullable(),
  drawnStrokes: drawnSignatureStrokesSchema.nullable(),
  fontName: z.enum(SIGNATURE_FONTS).nullable(),
  fontText: z.string().nullable(),
  signatureLine: z.string().nullable(),
});
export type SignatureSettings = z.infer<typeof signatureSettingsSchema>;

export const signatureSettingsResponseSchema = z.strictObject({
  settings: signatureSettingsSchema,
  updatedAt: z.string().nullable(),
});
export type SignatureSettingsResponse = z.infer<typeof signatureSettingsResponseSchema>;

/** `GET`/`PUT /api/orgs/members/:userId/signature-line` — the admin surface, narrower than the full
 * settings: an admin sees and edits the line, never the mark. */
export const memberSignatureLineResponseSchema = z.strictObject({
  userId: z.uuid(),
  signatureLine: z.string().nullable(),
});
export type MemberSignatureLineResponse = z.infer<typeof memberSignatureLineResponseSchema>;

export const SIGNATURE_ERROR_CODES = ['invalid_request', 'not_found'] as const;
export type SignatureErrorCode = (typeof SIGNATURE_ERROR_CODES)[number];
